import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {catchError, map, switchMap} from 'rxjs/operators';
import {API} from '@configs/api.config';
import { HttpClient, HttpParams } from '@angular/common/http';
import {Pageable} from '@shared/models/pageable.model';
import {JobFile} from '@core/models/job-file';
import {ShipmentOrderSpec} from '@core/models/job-file/shipment-order-spec.model';
import {ShipmentOrderSpecFilterRequest} from '../models/shipment-order-spec-filter-request.model';
import { FileDetails } from '../models/state/file-details.model';
import { PreSignedUrl } from '@control-tower/models/jpm/pre-signed-url';
import { UpdateShipmentOrderPropertiesDTO } from '../models/update-shipment-order-properties-dto.model';
import { CanDeleteAllOrdersResponse } from '../models/can-delete-all-orders-response.model';

/** Matches MAX_IDS_ONLY_RESULTS in ms-jobfile's ShipmentOrderCustomRepositoryImpl. */
export const MAX_SELECT_ALL_ORDERS = 10000;

@Injectable({
  providedIn: 'root'
})
export class OrderManagementService {

  constructor(private httpClient: HttpClient) { }

  loadShipmentOrderSpecs(orgId: string, pageNo: number, pageSize: number, searchKey): Observable<Pageable<ShipmentOrderSpec>> {
    let params = new HttpParams();
    params = params.set('searchKey', searchKey);
    const url = API.jobFile.loadShipmentOrderSpecs.format([orgId, pageNo.toString(), pageSize.toString()]);
    return this.httpClient.get<Pageable<ShipmentOrderSpec>>(url, {params});
  }

  uploadShipmentOrderExcel(file: File, orgId: string): Observable<ShipmentOrderSpec[]> {
    const data = new FormData();
    data.append('file', file, file.name);
    const url = API.jobFile.uploadShipmentOrderExcel.format([orgId]);
    return this.httpClient.post<ShipmentOrderSpec[]>(url, data);
  }

  uploadShipmentOrderExcelWithJob(file: File, orgId: string, jobRefId: string): Observable<ShipmentOrderSpec[] | ArrayBuffer> {
    const data = new FormData();
    data.append('file', file, file.name);
    const url = API.jobFile.uploadShipmentOrderExcelDomesticWithJob.format([orgId, jobRefId]);
    
    // Use blob responseType to handle both JSON and binary (Excel error report) responses
    return this.httpClient.post(url, data, { 
      observe: 'response',
      responseType: 'blob' as 'json'
    }).pipe(
      switchMap(async (response: any) => {
        const contentType = response.headers.get('content-type');
        
        // If response is JSON, parse it and return as ShipmentOrderSpec[]
        if (contentType && contentType.includes('application/json')) {
          const text = await response.body.text();
          return JSON.parse(text) as ShipmentOrderSpec[];
        }
        
        // Otherwise, it's a binary file (error report), convert to ArrayBuffer
        return await response.body.arrayBuffer();
      })
    );
  }

  checkDuplicateOrderIds(file: File, orgId: string, jobRefId: string): Observable<string[]> {
    const data = new FormData();
    data.append('file', file, file.name);
    const url = API.jobFile.checkDuplicateOrderIds.format([orgId, jobRefId]);
    return this.httpClient.post<string[]>(url, data);
  }

  addShipmentOrdersToJob(orgId: string, jobRefId: string, shipmentOrderSpecs: ShipmentOrderSpec[]): Observable<JobFile> {
    const url = API.jobFile.addShipmentOrdersToJob.format([orgId, jobRefId]);
    return this.httpClient.put<JobFile>(url, shipmentOrderSpecs);
  }

  mergeShipmentOrdersToJobByIds(orgId: string, jobRefId: string, shipmentOrderSpecIds: string[]): Observable<JobFile> {
    const url = API.jobFile.mergeShipmentOrdersToJob.format([orgId, jobRefId]);
    return this.httpClient.put<JobFile>(url, shipmentOrderSpecIds);
  }

  checkShipmentOrderCanDelete(shipmentOrderId: string): Observable<{canDelete: boolean, reason?: string}> {
    const url = API.jobFile.checkShipmentOrderCanDelete.format([shipmentOrderId]);
    return this.httpClient.get<{canDelete: boolean, reason?: string}>(url);
  }

  checkAllShipmentOrdersCanDelete(orgId: string): Observable<CanDeleteAllOrdersResponse> {
    const url = API.jobFile.checkAllShipmentOrdersCanDelete.format([orgId]);
    return this.httpClient.get<CanDeleteAllOrdersResponse>(url);
  }

  deleteShipmentOrder(shipmentOrderId: string) {
    const url = API.jobFile.deleteShipmentOrder.format([shipmentOrderId]);
    return this.httpClient.delete(url);
  }

  /**
   * Ids of every order the given filter matches, across all pages. Reuses the filter endpoint with
   * the `idsOnly` projection, so the response carries only id + orderId instead of full documents.
   */
  getFilteredShipmentOrderSpecIds(request: ShipmentOrderSpecFilterRequest): Observable<string[]> {
    const url = API.jobFile.filterShipmentOrderSpecs;
    // pageSize is sent as well as idsOnly on purpose. A backend that predates `idsOnly` ignores the
    // unknown field (Spring does not fail on unknown properties) and would otherwise answer with a
    // single page, silently under-selecting. With both, the id set is complete either way; the
    // newer backend ignores paging on this path.
    return this.httpClient
      .post<Pageable<ShipmentOrderSpec>>(url, {
        ...request,
        pageNo: 0,
        pageSize: MAX_SELECT_ALL_ORDERS,
        idsOnly: true
      })
      .pipe(map(page => (page?.content ?? []).map(order => order.id)));
  }

  filterShipmentOrderSpecs(shipmentOrderSpecFilterRequest: ShipmentOrderSpecFilterRequest): Observable<Pageable<ShipmentOrderSpec>> {
    const url = API.jobFile.filterShipmentOrderSpecs;
    return this.httpClient.post<Pageable<ShipmentOrderSpec>>(url, shipmentOrderSpecFilterRequest);
  }

  deleteAllShipmentOrderSpecs(orgId: string): Observable<void> {
    const url = API.jobFile.deleteAllShipmentOrderSpecs.format([orgId]);
    return this.httpClient.delete<void>(url);
  }

  deleteShipmentOrdersByIds(shipmentOrderIds: string[]): Observable<void> {
    const url = API.jobFile.deleteShipmentOrdersByIds;
    return this.httpClient.request<void>('DELETE', url, { body: shipmentOrderIds }).pipe(
      catchError(error => {
        console.error('Failed to delete shipment orders by IDs:', error);
        throw error;
      })
    );
  }

  uploadOrderDocument(orgId: string, orderId: string, file: File): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);
    const url = API.jobFile.uploadOrderDocument.format([orgId, orderId]);
    return this.httpClient.post<any>(url, formData);
  }

  getFilesByOrgAndOrder(orgId: string, orderId: string): Observable<FileDetails[]> {
    const url = API.fileHandelar.getFilesByOrgAndOrder.format([orgId, orderId]);
    return this.httpClient.get<FileDetails[]>(url);
  }

  deleteFile(fileId: string): Observable<void> {
    const url = API.fileHandelar.deleteFileMetadata.format([fileId]);
    return this.httpClient.delete<void>(url);
  }

  viewFile(fileId: string): Observable<FileDetails> {
    const url = API.fileHandelar.getPreSignedUrl.format([fileId]);
    return this.httpClient.get<PreSignedUrl>(url);
  }

  updateShipmentOrderPropertiesBatch(
    orgId: string,
    ids: string[],
    updateDTO: UpdateShipmentOrderPropertiesDTO
  ): Observable<ShipmentOrderSpec[]> {
    const url = API.jobFile.updateShipmentOrderPropertiesBatch.format([orgId]);
    let params = new HttpParams();
    ids.forEach(id => {
      params = params.append('ids', id);
    });
    return this.httpClient.patch<ShipmentOrderSpec[]>(url, updateDTO, { params });
  }
}
